// Per-user storage: routes all localStorage.* calls through the signed-in user namespace.
import { scopedStorage as localStorage } from "../services/userStorage";
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import {
  AlignLeft,
  BarChart3,
  BookMarked,
  BookOpen,
  Bookmark,
  Check,
  ChevronLeft,
  ChevronRight,
  Clock,
  Copy,
  Database,
  Menu,
  MessageSquare,
  Minus,
  Moon,
  Plus,
  Play,
  Square,
  Search,
  Settings2,
  Share2,
  Sparkles,
  Sun,
  X
} from 'lucide-react';
import StandardHeader from './StandardHeader';
import { bibleStructure, getChaptersForBook } from '../data/bibleData';
import {
  DEFAULT_BIBLE_TRANSLATION,
  getBibleTranslation
} from '../data/bibleTranslations';
import {
  addBookmark,
  addHighlight,
  addNote,
  getBibleManifest,
  getBookmarks,
  getChapter,
  getHighlights,
  getNotes,
  getRecentReadings,
  getReadingHeatmap,
  markVerseSeen,
  removeBookmark,
  removeHighlight,
  removeNote,
  searchBible
} from '../services/bibleReaderService';
import { playVerse, stopVerse } from '../services/ttsService';

const READER_PREFERENCES_KEY = 'bible_reader_preferences';
const highlightColors = [
  { id: 'yellow', label: 'Gold' },
  { id: 'green', label: 'Sage' },
  { id: 'blue', label: 'Sky' },
  { id: 'pink', label: 'Rose' }
];

const readPreferences = () => {
  try {
    return {
      fontSize: 20,
      lineHeight: 1.85,
      theme: 'paper',
      showVerseNumbers: true,
      ...JSON.parse(localStorage.getItem(READER_PREFERENCES_KEY) || '{}')
    };
  } catch {
    return { fontSize: 20, lineHeight: 1.85, theme: 'paper', showVerseNumbers: true };
  }
};

const EnhancedBibleReader = () => {
  const { bookId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialBook = bibleStructure.some((book) => book.id === bookId) ? bookId : 'JHN';
  const initialBookData = bibleStructure.find((book) => book.id === initialBook);
  const initialChapter = Math.min(
    initialBookData.chapters,
    Math.max(1, Number(searchParams.get('chapter')) || 1)
  );
  const initialVerse = Number(searchParams.get('verse')) || null;

  const [selectedBook, setSelectedBook] = useState(initialBook);
  const [selectedChapter, setSelectedChapter] = useState(initialChapter);
  const selectedVersion = DEFAULT_BIBLE_TRANSLATION;
  const [selectedVerse, setSelectedVerse] = useState(initialVerse);
  const [chapterContent, setChapterContent] = useState(null);
  const [manifest, setManifest] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerTab, setDrawerTab] = useState('books');
  const [bookFilter, setBookFilter] = useState('');
  const [testamentFilter, setTestamentFilter] = useState('all');
  const [displayOpen, setDisplayOpen] = useState(false);
  const [findOpen, setFindOpen] = useState(false);
  const [findQuery, setFindQuery] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [heatmapOpen, setHeatmapOpen] = useState(false);
  const [readingHeatmap, setReadingHeatmap] = useState(() => getReadingHeatmap(DEFAULT_BIBLE_TRANSLATION));
  const seenInSession = useRef(new Set());
  const [activeVerse, setActiveVerse] = useState(null);
  const [noteVerse, setNoteVerse] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [preferences, setPreferences] = useState(readPreferences);

  const [bookmarks, setBookmarks] = useState(() => getBookmarks());
  const [highlights, setHighlights] = useState(() => getHighlights());
  const [notes, setNotes] = useState(() => getNotes());
  const [recentReadings, setRecentReadings] = useState(() => getRecentReadings());
  const [speakingVerse, setSpeakingVerse] = useState(null);
  const linkedVerseActive = Boolean(selectedVerse);
  const [speechError, setSpeechError] = useState('');

  const selectedBookData = bibleStructure.find((book) => book.id === selectedBook) || bibleStructure[0];
  const selectedBookName = selectedBookData.name;
  const chapters = getChaptersForBook(selectedBook);
  const translation = getBibleTranslation(selectedVersion);
  const currentBookmark = bookmarks.find(
    (bookmark) =>
      bookmark.book === selectedBook &&
      bookmark.chapter === selectedChapter &&
      bookmark.version === selectedVersion &&
      !bookmark.verse
  );
  const filteredBooks = useMemo(() => {
    const query = bookFilter.trim().toLowerCase();
    return bibleStructure.filter((book) => {
      const matchesTestament = testamentFilter === 'all' || book.testament === testamentFilter;
      return matchesTestament && (!query || book.name.toLowerCase().includes(query));
    });
  }, [bookFilter, testamentFilter]);

  const matchingVerses = useMemo(() => {
    const query = findQuery.trim().toLowerCase();
    if (!query || !chapterContent) return [];
    return chapterContent.verses.filter((verse) => verse.text.toLowerCase().includes(query));
  }, [chapterContent, findQuery]);

  useEffect(() => {
    let active = true;
    getBibleManifest(selectedVersion).then((data) => {
      if (active) setManifest(data);
    }).catch(() => {
      if (active) setManifest(null);
    });
    return () => { active = false; };
  }, [selectedVersion]);

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    setError('');
    setActiveVerse(null);

    getChapter(selectedBook, selectedChapter, selectedVersion)
      .then((chapter) => {
        if (!active) return;
        setChapterContent(chapter);
        setRecentReadings(getRecentReadings());
      })
      .catch((chapterError) => {
        if (active) setError(chapterError.message || 'This chapter could not be loaded.');
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });

    return () => { active = false; };
  }, [selectedBook, selectedChapter, selectedVersion]);

  useEffect(() => {
    navigate(
      `/bible/reader/${selectedBook}?chapter=${selectedChapter}${selectedVerse ? `&verse=${selectedVerse}` : ''}`,
      { replace: true }
    );
  }, [navigate, selectedBook, selectedChapter, selectedVerse]);

  useEffect(() => {
    try {
      localStorage.setItem(READER_PREFERENCES_KEY, JSON.stringify(preferences));
    } catch {
      // Reading preferences are optional when storage is unavailable.
    }
  }, [preferences]);

  useEffect(() => {
    if (!selectedVerse || isLoading) return;
    const target = document.getElementById(`reader-verse-${selectedVerse}`);
    target?.scrollIntoView({ behavior: 'auto', block: 'center' });
  }, [isLoading, selectedVerse]);

  useEffect(() => {
    const query = searchQuery.trim();
    if (query.length < 2) {
      setSearchResults([]);
      setSearchLoading(false);
      return undefined;
    }
    let active = true;
    setSearchLoading(true);
    const timeout = window.setTimeout(() => {
      searchBible(query, selectedVersion, 40)
        .then((results) => { if (active) setSearchResults(results); })
        .catch(() => { if (active) setSearchResults([]); })
        .finally(() => { if (active) setSearchLoading(false); });
    }, 250);
    return () => { active = false; window.clearTimeout(timeout); };
  }, [searchQuery, selectedVersion]);

  useEffect(() => {
    if (isLoading || !chapterContent?.verses?.length) return undefined;
    const observer = new IntersectionObserver((entries) => {
      let heatmapChanged = false;
      entries.forEach((entry) => {
        if (!entry.isIntersecting || entry.intersectionRatio < 0.72) return;
        const verse = Number(entry.target.dataset.verse);
        const key = `${selectedBook}-${selectedChapter}-${verse}-${selectedVersion}`;
        if (!verse || seenInSession.current.has(key)) return;
        seenInSession.current.add(key);
        markVerseSeen(selectedBook, selectedChapter, verse, selectedVersion);
        heatmapChanged = true;
      });
      if (heatmapChanged) setReadingHeatmap(getReadingHeatmap(selectedVersion));
    }, { rootMargin: '-42% 0px -42% 0px', threshold: [0.72] });
    chapterContent.verses.forEach((verse) => {
      const element = document.getElementById(`reader-verse-${verse.number}`);
      if (!verse.omitted && element) observer.observe(element);
    });
    return () => observer.disconnect();
  }, [chapterContent, isLoading, selectedBook, selectedChapter, selectedVersion]);

  useEffect(() => () => stopVerse(), []);

  const navigateTo = (book, chapter, verse = null) => {
    setSelectedBook(book);
    setSelectedChapter(chapter);
    setSelectedVerse(verse);
    setDrawerOpen(false);
    setActiveVerse(null);
    if (verse) setSearchQuery('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goToPreviousChapter = () => {
    const bookIndex = bibleStructure.findIndex((book) => book.id === selectedBook);
    if (selectedChapter > 1) navigateTo(selectedBook, selectedChapter - 1);
    else if (bookIndex > 0) {
      const previousBook = bibleStructure[bookIndex - 1];
      navigateTo(previousBook.id, previousBook.chapters);
    }
  };

  const goToNextChapter = () => {
    const bookIndex = bibleStructure.findIndex((book) => book.id === selectedBook);
    if (selectedChapter < selectedBookData.chapters) navigateTo(selectedBook, selectedChapter + 1);
    else if (bookIndex < bibleStructure.length - 1) navigateTo(bibleStructure[bookIndex + 1].id, 1);
  };

  const toggleChapterBookmark = async () => {
    const updated = currentBookmark
      ? await removeBookmark(currentBookmark.id)
      : await addBookmark(selectedBook, selectedChapter, null, selectedVersion);
    setBookmarks(updated);
  };

  const getHighlight = (verseNumber) => highlights.find(
    (highlight) =>
      highlight.book === selectedBook &&
      highlight.chapter === selectedChapter &&
      highlight.verse === verseNumber &&
      highlight.version === selectedVersion
  );

  const getNote = (verseNumber) => notes.find(
    (note) =>
      note.book === selectedBook &&
      note.chapter === selectedChapter &&
      note.verse === verseNumber &&
      note.version === selectedVersion
  );

  const setVerseHighlight = async (verseNumber, color) => {
    const updated = await addHighlight(selectedBook, selectedChapter, verseNumber, color, selectedVersion);
    setHighlights(updated);
  };

  const clearVerseHighlight = async (verseNumber) => {
    const highlight = getHighlight(verseNumber);
    if (highlight) setHighlights(await removeHighlight(highlight.id));
  };

  const openNote = (verseNumber) => {
    setNoteVerse(verseNumber);
    setNoteText(getNote(verseNumber)?.text || '');
  };

  const saveNote = async () => {
    if (!noteText.trim()) return;
    setNotes(await addNote(selectedBook, selectedChapter, noteVerse, noteText.trim(), selectedVersion));
    setNoteVerse(null);
    setNoteText('');
  };

  const deleteNote = async () => {
    const note = getNote(noteVerse);
    if (note) setNotes(await removeNote(note.id));
    setNoteVerse(null);
    setNoteText('');
  };

  const formatVerse = (verse) =>
    `${verse.text}\n\n${selectedBookName} ${selectedChapter}:${verse.number} (${translation?.abbreviation || selectedVersion})`;

  const copyVerse = async (verse) => {
    await navigator.clipboard?.writeText(formatVerse(verse));
    setActiveVerse(null);
  };

  const shareVerse = async (verse) => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: `${selectedBookName} ${selectedChapter}:${verse.number}`,
          text: formatVerse(verse)
        });
      } else {
        await copyVerse(verse);
      }
      setActiveVerse(null);
    } catch {
      // Dismissing the native share sheet is not an error the reader needs to surface.
    }
  };

  const toggleVerseSpeech = async (verse) => {
    if (speakingVerse === verse.number) {
      stopVerse();
      setSpeakingVerse(null);
      return;
    }
    setSpeechError('');
    setSpeakingVerse(verse.number);
    try {
      await playVerse({
        text: verse.text,
        reference: `${selectedBookName} ${selectedChapter}:${verse.number}`,
        onEnd: () => setSpeakingVerse(null),
        onError: () => { setSpeakingVerse(null); setSpeechError('Speech playback stopped unexpectedly.'); }
      });
    } catch (speechFailure) {
      setSpeakingVerse(null);
      setSpeechError(speechFailure.message || 'Speech playback is unavailable.');
    }
  };

  const openSavedReading = (item) => navigateTo(item.book, item.chapter, item.verse || null);

  return (
    <div className={`reader-app reader-theme-${preferences.theme}`}>
      <StandardHeader
        title="Bible Reader"
        icon={BookOpen}
        color="#344b40"
        actions={(
          <Link className="module-text-control" to="/devotionals">
            <Sparkles size={16} /> <span>Daily Dose</span>
          </Link>
        )}
      />

      <div className="reader-command-bar">
        <button className="reader-icon-button reader-menu-button" onClick={() => setDrawerOpen(true)} aria-label="Open Bible navigation">
          <Menu size={21} />
        </button>
        <button className="reader-reference-button" onClick={() => setDrawerOpen(true)}>
          <span>{selectedBookName} {selectedChapter}</span>
          <small>{translation?.abbreviation} · {selectedBookData.testament === 'OT' ? 'Old Testament' : 'New Testament'}</small>
        </button>
        <div className="reader-chapter-arrows">
          <button onClick={goToPreviousChapter} disabled={selectedBook === 'GEN' && selectedChapter === 1} aria-label="Previous chapter">
            <ChevronLeft size={21} />
          </button>
          <button onClick={goToNextChapter} disabled={selectedBook === 'REV' && selectedChapter === 22} aria-label="Next chapter">
            <ChevronRight size={21} />
          </button>
        </div>
        <button className="reader-icon-button" onClick={() => setFindOpen(!findOpen)} aria-label="Find in chapter">
          <Search size={19} />
        </button>
        <button className={`reader-icon-button ${currentBookmark ? 'is-active' : ''}`} onClick={toggleChapterBookmark} aria-label="Bookmark chapter">
          {currentBookmark ? <BookMarked size={19} /> : <Bookmark size={19} />}
        </button>
        <button className="reader-icon-button" onClick={() => setHeatmapOpen(true)} aria-label="Open reading heat map">
          <BarChart3 size={19} />
        </button>
        <button className="reader-icon-button" onClick={() => setDisplayOpen(true)} aria-label="Reading display settings">
          <Settings2 size={19} />
        </button>
      </div>

      {findOpen && (
        <div className="reader-find-bar">
          <Search size={17} />
          <input
            autoFocus
            value={findQuery}
            onChange={(event) => setFindQuery(event.target.value)}
            placeholder={`Find in ${selectedBookName} ${selectedChapter}`}
          />
          {findQuery && <span>{matchingVerses.length} found</span>}
          <button onClick={() => { setFindOpen(false); setFindQuery(''); }} aria-label="Close find">
            <X size={18} />
          </button>
        </div>
      )}

      <section className="reader-global-search" aria-label="Search the whole Bible">
        <div>
          <Search size={18} />
          <input value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)} placeholder="Search the Bible or type John 3:16" />
        </div>
        {searchLoading && <small>Searching indexed local Bible…</small>}
        {!searchLoading && searchQuery.trim().length > 1 && <small>{searchResults.length} result{searchResults.length === 1 ? '' : 's'}</small>}
        {searchResults.length > 0 && (
          <div className="reader-search-results">
            {searchResults.map((result) => (
              <button key={result.id} onClick={() => navigateTo(result.book, result.chapter, result.verse)}>
                <strong>{result.reference}</strong>
                <span>{result.text}</span>
              </button>
            ))}
          </div>
        )}
      </section>

      <main className="reader-main">
        <div className="reader-progress" aria-hidden="true">
          <span style={{ width: `${(selectedChapter / selectedBookData.chapters) * 100}%` }} />
        </div>

        <article
          className="reader-paper"
          style={{
            '--reader-font-size': `${preferences.fontSize}px`,
            '--reader-line-height': preferences.lineHeight
          }}
        >
          <header className="reader-chapter-header">
            <p>{selectedBookData.testament === 'OT' ? 'Old Testament' : 'New Testament'} · Chapter {selectedChapter} of {selectedBookData.chapters}</p>
            <h1>{selectedBookName} <span>{selectedChapter}</span></h1>
            <div className="reader-source-line">
              <Database size={14} />
              <span>{translation?.name} · Verified local text · {translation?.license}</span>
            </div>
          </header>

          {isLoading ? (
            <div className="reader-state">
              <div className="reader-spinner" />
              <strong>Opening {selectedBookName} {selectedChapter}</strong>
            </div>
          ) : error ? (
            <div className="reader-state reader-error">
              <strong>Chapter unavailable</strong>
              <p>{error}</p>
            </div>
          ) : (
            <div className={`reader-verses ${linkedVerseActive ? 'has-linked-background' : ''}`}>
              {linkedVerseActive && <div className="reader-linked-callout">Linked verse focus · scroll or tap any verse to return to normal chapter reading.</div>}
              {speechError && <div className="reader-speech-error" role="alert">{speechError}</div>}
              {chapterContent?.verses.map((verse) => {
                const highlight = getHighlight(verse.number);
                const note = getNote(verse.number);
                const matchesFind = findQuery && matchingVerses.some((match) => match.number === verse.number);
                return (
                  <section
                    id={`reader-verse-${verse.number}`}
                    key={verse.number}
                    data-verse={verse.number}
                    className={`reader-verse ${highlight ? `reader-highlight-${highlight.color}` : ''} ${matchesFind ? 'is-find-match' : ''} ${activeVerse === verse.number ? 'is-selected' : ''} ${selectedVerse === verse.number ? 'is-linked-verse' : ''} ${speakingVerse === verse.number ? 'is-speaking' : ''} ${verse.omitted ? 'is-omitted' : ''}`}
                    onClick={() => { if (!verse.omitted) { setSelectedVerse(null); setActiveVerse(activeVerse === verse.number ? null : verse.number); } }}
                  >
                    {preferences.showVerseNumbers && <sup>{verse.number}</sup>}
                    {verse.omitted ? (
                      <span className="reader-omitted-text">Verse omitted in this translation.</span>
                    ) : (
                      <span>{verse.text}</span>
                    )}
                    {note && <MessageSquare className="reader-note-mark" size={14} aria-label="Verse has a note" />}
                    {!verse.omitted && (
                      <button className="reader-speak-button" onClick={(event) => { event.stopPropagation(); toggleVerseSpeech(verse); }} aria-label={`${speakingVerse === verse.number ? 'Stop' : 'Play'} ${selectedBookName} ${selectedChapter}:${verse.number}`}>
                        {speakingVerse === verse.number ? <Square size={13} fill="currentColor" /> : <Play size={14} fill="currentColor" />}
                      </button>
                    )}

                    {activeVerse === verse.number && (
                      <div className="reader-verse-tools" onClick={(event) => event.stopPropagation()}>
                        <button onClick={() => openNote(verse.number)}><MessageSquare size={16} /> {note ? 'Edit note' : 'Note'}</button>
                        <div className="reader-color-tools" aria-label="Highlight colors">
                          {highlightColors.map((color) => (
                            <button
                              key={color.id}
                              className={`reader-color reader-color-${color.id} ${highlight?.color === color.id ? 'is-active' : ''}`}
                              onClick={() => setVerseHighlight(verse.number, color.id)}
                              aria-label={`${color.label} highlight`}
                            >
                              {highlight?.color === color.id && <Check size={12} />}
                            </button>
                          ))}
                          {highlight && <button className="reader-clear-highlight" onClick={() => clearVerseHighlight(verse.number)}>Clear</button>}
                        </div>
                        <button onClick={() => copyVerse(verse)}><Copy size={16} /> Copy</button>
                        <button onClick={() => shareVerse(verse)}><Share2 size={16} /> Share</button>
                        <button onClick={() => toggleVerseSpeech(verse)}>{speakingVerse === verse.number ? <Square size={15} /> : <Play size={16} />} {speakingVerse === verse.number ? 'Stop' : 'Listen'}</button>
                        <button onClick={() => setActiveVerse(null)} aria-label="Close verse tools"><X size={16} /> Close</button>
                      </div>
                    )}
                  </section>
                );
              })}
            </div>
          )}

          {!isLoading && !error && (
            <footer className="reader-chapter-footer">
              <button onClick={goToPreviousChapter} disabled={selectedBook === 'GEN' && selectedChapter === 1}>
                <ChevronLeft size={20} /><span><small>Previous</small>{selectedChapter > 1 ? `${selectedBookName} ${selectedChapter - 1}` : 'Previous book'}</span>
              </button>
              <button onClick={goToNextChapter} disabled={selectedBook === 'REV' && selectedChapter === 22}>
                <span><small>Next</small>{selectedChapter < selectedBookData.chapters ? `${selectedBookName} ${selectedChapter + 1}` : 'Next book'}</span><ChevronRight size={20} />
              </button>
            </footer>
          )}
        </article>
      </main>

      {drawerOpen && (
        <div className="reader-overlay" onClick={() => setDrawerOpen(false)}>
          <aside className="reader-drawer" onClick={(event) => event.stopPropagation()}>
            <div className="reader-sheet-header">
              <div><small>Go to</small><h2>{selectedBookName} {selectedChapter}</h2></div>
              <button onClick={() => setDrawerOpen(false)} aria-label="Close navigation"><X size={21} /></button>
            </div>
            <nav className="reader-tabs" aria-label="Bible navigation">
              <button className={drawerTab === 'books' ? 'is-active' : ''} onClick={() => setDrawerTab('books')}><BookOpen size={17} /> Bible</button>
              <button className={drawerTab === 'saved' ? 'is-active' : ''} onClick={() => setDrawerTab('saved')}><BookMarked size={17} /> Saved</button>
              <button className={drawerTab === 'recent' ? 'is-active' : ''} onClick={() => setDrawerTab('recent')}><Clock size={17} /> Recent</button>
            </nav>

            <div className="reader-drawer-content">
              {drawerTab === 'books' && (
                <>
                  <div className="reader-book-filter">
                    <Search size={17} />
                    <input value={bookFilter} onChange={(event) => setBookFilter(event.target.value)} placeholder="Find a book" />
                  </div>
                  <div className="reader-testament-filter">
                    {[['all', 'All 66'], ['OT', 'Old Testament'], ['NT', 'New Testament']].map(([id, label]) => (
                      <button key={id} className={testamentFilter === id ? 'is-active' : ''} onClick={() => setTestamentFilter(id)}>{label}</button>
                    ))}
                  </div>
                  <div className="reader-book-grid">
                    {filteredBooks.map((book) => (
                      <button
                        key={book.id}
                        className={selectedBook === book.id ? 'is-active' : ''}
                        onClick={() => { setSelectedBook(book.id); setSelectedChapter(1); }}
                      >
                        <span>{book.name}</span><small>{book.chapters} ch</small>
                      </button>
                    ))}
                  </div>
                  <div className="reader-chapter-picker">
                    <div><strong>{selectedBookName}</strong><span>Choose a chapter</span></div>
                    <div>
                      {chapters.map((chapter) => (
                        <button key={chapter} className={selectedChapter === chapter ? 'is-active' : ''} onClick={() => navigateTo(selectedBook, chapter)}>{chapter}</button>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {drawerTab === 'saved' && (
                <SavedList items={bookmarks} empty="Bookmark a chapter to keep it close." icon={BookMarked} onOpen={openSavedReading} />
              )}
              {drawerTab === 'recent' && (
                <SavedList items={recentReadings} empty="Chapters you read will appear here." icon={Clock} onOpen={openSavedReading} />
              )}
            </div>
          </aside>
        </div>
      )}

      {displayOpen && (
        <div className="reader-overlay reader-overlay-bottom" onClick={() => setDisplayOpen(false)}>
          <section className="reader-display-sheet" onClick={(event) => event.stopPropagation()}>
            <div className="reader-sheet-header">
              <div><small>Reading comfort</small><h2>Display</h2></div>
              <button onClick={() => setDisplayOpen(false)} aria-label="Close display settings"><X size={21} /></button>
            </div>
            <div className="reader-setting-row">
              <div><strong>Text size</strong><span>{preferences.fontSize}px</span></div>
              <div className="reader-stepper">
                <button onClick={() => setPreferences({ ...preferences, fontSize: Math.max(16, preferences.fontSize - 2) })} aria-label="Decrease text size"><Minus size={18} /></button>
                <span>Abc</span>
                <button onClick={() => setPreferences({ ...preferences, fontSize: Math.min(30, preferences.fontSize + 2) })} aria-label="Increase text size"><Plus size={18} /></button>
              </div>
            </div>
            <div className="reader-setting-row">
              <div><strong>Line spacing</strong><span>Give the words room</span></div>
              <div className="reader-stepper">
                <button onClick={() => setPreferences({ ...preferences, lineHeight: Math.max(1.5, preferences.lineHeight - 0.15) })}><Minus size={18} /></button>
                <AlignLeft size={18} />
                <button onClick={() => setPreferences({ ...preferences, lineHeight: Math.min(2.3, preferences.lineHeight + 0.15) })}><Plus size={18} /></button>
              </div>
            </div>
            <div className="reader-setting-row">
              <div><strong>Page color</strong><span>Choose what rests your eyes</span></div>
              <div className="reader-theme-buttons">
                <button className={preferences.theme === 'paper' ? 'is-active' : ''} onClick={() => setPreferences({ ...preferences, theme: 'paper' })}><Sun size={17} /> Paper</button>
                <button className={preferences.theme === 'sepia' ? 'is-active' : ''} onClick={() => setPreferences({ ...preferences, theme: 'sepia' })}>Sepia</button>
                <button className={preferences.theme === 'night' ? 'is-active' : ''} onClick={() => setPreferences({ ...preferences, theme: 'night' })}><Moon size={17} /> Night</button>
              </div>
            </div>
            <label className="reader-toggle-row">
              <span><strong>Verse numbers</strong><small>Show verse markers while reading</small></span>
              <input type="checkbox" checked={preferences.showVerseNumbers} onChange={(event) => setPreferences({ ...preferences, showVerseNumbers: event.target.checked })} />
            </label>
            {manifest && (
              <div className="reader-data-card">
                <Database size={19} />
                <div><strong>{manifest.translation.name}</strong><span>{manifest.totals.books} books · {manifest.totals.chapters.toLocaleString()} chapters · {manifest.totals.verses.toLocaleString()} verse slots</span><small>{manifest.translation.license} · Source verified by SHA-256</small></div>
              </div>
            )}
          </section>
        </div>
      )}


      {heatmapOpen && (
        <div className="reader-overlay" onClick={() => setHeatmapOpen(false)}>
          <section className="reader-note-dialog reader-heatmap-dialog" onClick={(event) => event.stopPropagation()}>
            <div className="reader-sheet-header">
              <div><small>Reading coverage</small><h2>Verse heat map</h2></div>
              <button onClick={() => setHeatmapOpen(false)} aria-label="Close heat map"><X size={21} /></button>
            </div>
            <div className="reader-heatmap-grid">
              {readingHeatmap.map((item) => {
                const intensity = Math.min(4, Math.ceil(item.seen / Math.max(1, item.chapters * 2)));
                return <button key={item.book} className={`heat-${intensity}`} onClick={() => navigateTo(item.book, 1)} title={`${item.name}: ${item.seen} verses seen`}><span>{item.book}</span><small>{item.seen}</small></button>;
              })}
            </div>
          </section>
        </div>
      )}

      {noteVerse && (
        <div className="reader-overlay" onClick={() => setNoteVerse(null)}>
          <section className="reader-note-dialog" onClick={(event) => event.stopPropagation()}>
            <div className="reader-sheet-header">
              <div><small>Personal note</small><h2>{selectedBookName} {selectedChapter}:{noteVerse}</h2></div>
              <button onClick={() => setNoteVerse(null)} aria-label="Close note"><X size={21} /></button>
            </div>
            <textarea autoFocus value={noteText} onChange={(event) => setNoteText(event.target.value)} placeholder="Write what you want to remember..." />
            <div className="reader-note-actions">
              {getNote(noteVerse) && <button className="reader-delete-button" onClick={deleteNote}>Delete note</button>}
              <button className="reader-primary-button" onClick={saveNote} disabled={!noteText.trim()}>Save note</button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

const SavedList = ({ items, empty, icon: Icon, onOpen }) => (
  <div className="reader-saved-list">
    {!items.length ? (
      <div className="reader-empty"><Icon size={28} /><p>{empty}</p></div>
    ) : items.map((item) => (
      <button key={item.id} onClick={() => onOpen(item)}>
        <Icon size={18} />
        <span><strong>{item.reference}</strong><small>{item.version} · {new Date(item.timestamp).toLocaleDateString()}</small></span>
        <ChevronRight size={17} />
      </button>
    ))}
  </div>
);

export default EnhancedBibleReader;
